// src/App.js
// 测试自动部署功能 - 2025-12-08
import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import { analyzeVisual } from './zhipu';
import ThreeFishVideo from './ThreeFishVideo'; // Three.js 专家方案

// 预设的好奇探索任务库
const TASKS = [
  "拍一个你觉得最特别的东西，我想看看人类眼中的特别是什么样子的。",
  "拍一张你现在的环境，让我看看你生活的地方。",
  "拍一个让你想起美好回忆的东西，我想了解你的故事。",
  "拍窗外的风景，我想看看你眼中的世界。",
  "拍一个你觉得有趣的东西，我想知道什么能让你觉得有趣。",
  "拍你正在做的事情，让我看看人类的一天是怎么过的。",
  "拍一个让你感到温暖的东西，我想感受一下人类的温度。",
  "拍你今天的食物，我想知道人类都吃些什么。",
  "拍一个让你感到平静的地方，我想知道哪里能让你放松。",
  "拍一个你想分享给我的东西，我想了解你的世界。"
];

// 随机获取一个任务
const getRandomTask = () => {
  return TASKS[Math.floor(Math.random() * TASKS.length)];
};


// 初始状态：如果没有上传视频，显示空状态或默认视频
// 你可以在 public 文件夹放一个 default_bg.mp4 作为初始背景 

function App() {
  // --- 状态管理 ---
  // playlist 结构: [{ id, url, roast (观察/评论), task }]
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState(getRandomTask()); // 当前推荐的任务（一直显示在UI上）
  const [nextRecommendedTask, setNextRecommendedTask] = useState(getRandomTask()); // 推荐的下一个任务
  const [taskListExpanded, setTaskListExpanded] = useState(false); // 任务清单是否展开（默认折叠）
  const [isTaskMode, setIsTaskMode] = useState(false); // 是否处于任务模式（上传视频时使用）
  
  // 隐藏的文件输入框
  const fileInputRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const [showCameraPreview, setShowCameraPreview] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' 后置, 'user' 前置
  
  

  // 计算任务状态（只统计有任务标记的视频）
  const completedTasks = playlist.map(item => item.task).filter(Boolean);
  const allTasks = TASKS;
  const pendingTasks = allTasks.filter(task => !completedTasks.includes(task));
  
  // 获取当前播放视频对应的任务
  const currentPlayingTask = playlist[currentIndex]?.task || null;

  // 获取当前正在播放的内容
  const currentItem = playlist[currentIndex];
  
  // 调试日志：监听 playlist 和 currentIndex 变化
  useEffect(() => {
    console.log("==== 播放列表状态更新 ====");
    console.log("播放列表长度:", playlist.length);
    console.log("当前索引:", currentIndex);
    console.log("当前项:", currentItem);
    console.log("当前项有观察:", !!currentItem?.roast);
    console.log("Loading 状态:", loading);
    console.log("========================");
  }, [playlist, currentIndex, currentItem, loading]);

  // 当播放列表变化时，更新推荐任务
  useEffect(() => {
    const currentCompleted = playlist.map(item => item.task).filter(Boolean);
    const remainingTasks = TASKS.filter(t => !currentCompleted.includes(t));
    if (remainingTasks.length > 0) {
      setNextRecommendedTask(remainingTasks[Math.floor(Math.random() * remainingTasks.length)]);
    } else {
      setNextRecommendedTask(getRandomTask());
    }
  }, [playlist]);

  // --- 请求摄像头流 ---
  const requestCameraStream = async (facing = 'environment') => {
    // 停止当前流
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    
    // 请求新的视频流
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1920, min: 1280 },
        height: { ideal: 1080, min: 720 },
        facingMode: facing, // 'environment' 后置, 'user' 前置
        aspectRatio: { ideal: 16/9 }
      },
      audio: true
    });
    
    mediaStreamRef.current = stream;
    
    return stream;
  };

  // --- 摄像头捕获（高分辨率）---
  const handleCameraCapture = async (isTaskModeCapture = false) => {
    try {
      // 先显示预览界面
      setShowCameraPreview(true);
      
      // 等待界面渲染完成后再请求摄像头流
      setTimeout(async () => {
        try {
          // 使用当前设置的摄像头方向
          const stream = await requestCameraStream(facingMode);
          
          // 更新视频元素
          const video = videoPreviewRef.current;
          if (video) {
            video.srcObject = stream;
            video.play().catch(err => {
              console.error("视频播放失败:", err);
            });
          } else {
            console.error("视频元素不存在");
          }
        } catch (err) {
          console.error("获取摄像头流失败:", err);
          setShowCameraPreview(false);
          alert('无法访问摄像头，请检查权限设置');
        }
      }, 100);
      
    } catch (err) {
      console.error("无法访问摄像头:", err);
      setShowCameraPreview(false);
      // 如果摄像头访问失败，回退到文件选择
      if (isTaskModeCapture) {
        setIsTaskMode(true);
      }
      fileInputRef.current.click();
    }
  };

  // --- 切换前后置摄像头 ---
  const switchCamera = async () => {
    if (isRecording) return; // 录制中不能切换
    
    try {
      const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
      const stream = await requestCameraStream(newFacingMode);
      setFacingMode(newFacingMode);
      
      // 更新视频元素
      const video = videoPreviewRef.current;
      if (video) {
        video.srcObject = stream;
        video.play().catch(err => {
          console.error("视频播放失败:", err);
        });
      }
    } catch (err) {
      console.error("切换摄像头失败:", err);
      alert('切换摄像头失败，请重试');
    }
  };

  // --- 停止摄像头并录制视频 ---
  const stopCameraAndRecord = async (isTaskModeRecord = false) => {
    if (!mediaStreamRef.current || isRecording) return;
    
    try {
      const stream = mediaStreamRef.current;
      setIsRecording(true);
      setRecordingTime(0);
      
      // 检查支持的 MIME 类型
      let mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = ''; // 使用默认
      }
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        videoBitsPerSecond: 5000000 // 5Mbps，高质量
      });
      
      const chunks = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      // 开始计时
      let timeElapsed = 0;
      recordingTimerRef.current = setInterval(() => {
        timeElapsed += 0.1;
        setRecordingTime(Math.floor(timeElapsed * 10) / 10);
      }, 100);
      
      return new Promise((resolve, reject) => {
        mediaRecorder.onstop = () => {
          // 清除计时器
          if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
          }
          
          const blob = new Blob(chunks, { type: mimeType || 'video/webm' });
          
          // 停止所有轨道
          stream.getTracks().forEach(track => track.stop());
          mediaStreamRef.current = null;
          setShowCameraPreview(false);
          setIsRecording(false);
          setRecordingTime(0);
          
          // 创建文件对象，模拟文件上传
          const file = new File([blob], 'camera-video.webm', { type: mimeType || 'video/webm' });
          const syntheticEvent = {
            target: {
              files: [file]
            }
          };
          
          if (isTaskModeRecord) {
            setIsTaskMode(true);
          }
          
          // 调用原有的上传处理函数
          handleFileUpload(syntheticEvent).then(resolve).catch(reject);
        };
        
        mediaRecorder.onerror = (err) => {
          // 清除计时器
          if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
          }
          setIsRecording(false);
          setRecordingTime(0);
          reject(err);
        };
        
        mediaRecorder.start();
        
        // 录制5秒
        setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
        }, 5000);
      });
    } catch (err) {
      console.error("录制失败:", err);
      // 清除计时器
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      // 停止流
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
      setShowCameraPreview(false);
      setIsRecording(false);
      setRecordingTime(0);
      throw err;
    }
  };

  // --- 取消摄像头预览 ---
  const cancelCameraPreview = () => {
    // 清除计时器
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    setShowCameraPreview(false);
    setIsRecording(false);
    setRecordingTime(0);
  };
  
  // 格式化时间码（秒转 MM:SS）
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };


  // --- 核心逻辑 1: 视频上传与帧提取 ---
const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      console.log("没有选择文件");
      return;
    }

    console.log("========== 开始处理视频 ==========");
    setLoading(true);

    // 设置全局超时保护（30秒）
    const globalTimeout = setTimeout(() => {
      console.error("❌ 全局超时：视频处理时间过长");
      setLoading(false);
      alert('视频处理超时（30秒），请尝试以下方法：\n1. 使用更小的视频文件\n2. 使用更短的视频（< 10秒）\n3. 刷新页面重试');
    }, 30000);

    try {
      // 检查文件大小，如果太大先提示（但允许继续）
      const fileSizeMB = file.size / (1024 * 1024);
      console.log(`视频文件大小: ${fileSizeMB.toFixed(2)}MB`);
      console.log(`视频文件类型: ${file.type}`);
      
      if (fileSizeMB > 50) {
        const proceed = window.confirm(
          `视频文件较大（${fileSizeMB.toFixed(1)}MB），处理可能需要更长时间。是否继续？`
        );
        if (!proceed) {
          clearTimeout(globalTimeout);
          setLoading(false);
          return;
        }
      }

      // 1. 创建视频的本地 Blob URL
      const videoUrl = URL.createObjectURL(file);
      console.log("✅ 视频 Blob URL 创建成功");

      // 2. 提取视频第一帧（快速方案）
      console.log("开始提取视频帧...");
      const frameBase64 = await extractFrameFromVideoFast(videoUrl);
      console.log("✅ 视频帧提取成功，开始调用AI...");

      // 3. 根据模式决定提示词
      const prompt = isTaskMode 
        ? currentTask 
        : "随便看看，分享一下你的观察和感受就好，不用管什么任务。";
      
      console.log(`使用提示词: ${prompt.substring(0, 50)}...`);
      
      // 4. 发送给 GLM-4.5V
      console.log("调用AI分析...");
      let aiRoast = "";
      
      try {
        aiRoast = await analyzeVisual(frameBase64, prompt);
        console.log("✅ AI分析完成");
        console.log("AI 返回的观察:", aiRoast);
      } catch (aiError) {
        console.error("AI 调用失败:", aiError);
        // AI 调用失败时使用默认消息，但不中断流程
        aiRoast = "（Floater 正在思考中...可能网络不太好）";
      }
      
      // 确保观察不为空
      if (!aiRoast || aiRoast.trim() === '') {
        console.warn("⚠️ AI 返回空观察，使用默认值");
        aiRoast = "（Floater 好奇地看着，但不知道说什么好...）";
      }

      console.log("创建新的视频项...");
      
      // 5. 加入播放列表
      const newItem = {
        id: Date.now(),
        url: videoUrl,
        roast: aiRoast,
        task: isTaskMode ? currentTask : null
      };
      
      console.log("✅ 新视频项创建成功:", newItem.id);
      
      // 6. 如果是任务模式，完成任务后退出任务模式并更新推荐任务
      if (isTaskMode) {
        console.log("任务模式：更新任务状态");
        setIsTaskMode(false);
        const currentCompleted = playlist.map(item => item.task).filter(Boolean);
        const remainingTasks = TASKS.filter(t => !currentCompleted.includes(t) && t !== currentTask);
        if (remainingTasks.length > 0) {
          const newTask = remainingTasks[Math.floor(Math.random() * remainingTasks.length)];
          setCurrentTask(newTask);
          setNextRecommendedTask(newTask);
          console.log("✅ 任务已更新");
        } else {
          const newTask = getRandomTask();
          setCurrentTask(newTask);
          setNextRecommendedTask(newTask);
          console.log("✅ 所有任务完成，重新随机");
        }
      }

      console.log("添加到播放列表...");
      
      setPlaylist(prev => {
        const newList = [...prev, newItem];
        console.log(`✅ 播放列表已更新，共 ${newList.length} 个视频`);
        
        // 如果这是第一个视频，立即播放它
        if (newList.length === 1) {
          console.log("这是第一个视频，设置为播放");
          setCurrentIndex(0);
        } else {
          // 自动切换到最新上传的这个视频
          console.log(`切换到最新视频（索引 ${newList.length - 1}）`);
          setCurrentIndex(newList.length - 1);
        }
        return newList;
      });
      
      console.log("✅✅✅ 视频处理完成！");

    } catch (err) {
      console.error("❌❌❌ 处理视频失败:", err);
      console.error("错误堆栈:", err.stack);
      
      // 提供更详细的错误信息
      const errorMessage = err.message || "未知错误";
      let userMessage = "视频处理失败";
      
      if (errorMessage.includes("超时")) {
        userMessage = "视频处理超时，请尝试较小的视频文件";
      } else if (errorMessage.includes("格式") || errorMessage.includes("不支持")) {
        userMessage = "视频格式不支持，请尝试 MP4 格式";
      } else if (errorMessage.includes("损坏") || errorMessage.includes("无法读取")) {
        userMessage = "视频文件可能损坏，请换一个视频试试";
      } else if (errorMessage.includes("尺寸无效") || errorMessage.includes("画面")) {
        userMessage = "无法从视频提取画面，请换一个视频试试";
      } else {
        userMessage = `视频处理失败: ${errorMessage}`;
      }
      
      alert(userMessage);
    } finally {
      // 确保清除全局超时
      clearTimeout(globalTimeout);
      // 确保 loading 状态被重置
      console.log("重置 loading 状态");
      setLoading(false);
      console.log("========== 视频处理流程结束 ==========");
    }
  };

  // --- 快速提取视频帧（速度优先版本）---
  const extractFrameFromVideoFast = (videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      
      let resolved = false;
      let extractionAttempted = false;
      
      // 使用较短的超时，避免用户等待过久
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.warn("⏱️ 视频帧提取超时（8秒），尝试备用方法...");
          video.remove();
          // 如果超时，尝试使用备用方法
          extractFrameFromVideoFallback(videoUrl)
            .then(resolve)
            .catch((err) => {
              console.error("备用方法也失败:", err);
              reject(new Error('视频帧提取超时，请尝试：\n1. 更小的视频文件（< 10MB）\n2. 更短的视频（< 10秒）\n3. MP4格式的视频'));
            });
        }
      }, 8000); // 8秒超时，如果失败快速切换到备用方法

      const cleanup = () => {
        clearTimeout(timeout);
        if (!resolved) {
          resolved = true;
          setTimeout(() => {
            try {
              video.remove();
            } catch (e) {
              // 忽略清理错误
            }
          }, 100);
        }
      };

      // Seek 到合适位置提取帧（避免黑屏）
      const trySeekExtraction = () => {
        if (resolved || extractionAttempted) return;
        
        try {
          if (video.readyState >= 2 && video.videoWidth > 0 && video.duration > 0) {
            extractionAttempted = true;
            // 确保seek到至少0.5秒，避免黑屏
            if (video.duration > 0.5) {
              video.currentTime = 0.5;
            } else if (video.duration > 0.1) {
              video.currentTime = video.duration / 2;
            } else {
              video.currentTime = 0;
            }
          }
        } catch (err) {
          extractionAttempted = false;
        }
      };

      // 监听元数据加载完成
      video.onloadedmetadata = () => {
        if (resolved) return;
        // 尝试seek到0.5秒（避免黑屏）
        trySeekExtraction();
      };

      // 监听数据加载完成
      video.onloadeddata = () => {
        if (resolved) return;
        // 如果还没开始seek，现在尝试
        if (!extractionAttempted) {
          trySeekExtraction();
        }
      };

      // 监听可以播放
      video.oncanplay = () => {
        if (resolved) return;
        // 如果还没开始seek，现在尝试
        if (!extractionAttempted) {
          trySeekExtraction();
        }
      };

      // 监听 seek 完成 - 这是主要的提取时机
      video.onseeked = () => {
        if (resolved) return;
        try {
          const width = video.videoWidth || 640;
          const height = video.videoHeight || 480;
          
          if (width === 0 || height === 0) {
            if (!extractionAttempted) {
              // 如果还没尝试过，再试一次seek
              trySeekExtraction();
            } else {
              cleanup();
              reject(new Error('视频尺寸无效'));
            }
            return;
          }
          
          // 等待一小段时间，确保视频帧已经渲染（避免黑屏）
          setTimeout(() => {
            if (resolved) return;
            try {
              const canvas = document.createElement('canvas');
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              
              try {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                
                // 检查是否是黑屏（通过检查像素值）
                const imageData = ctx.getImageData(0, 0, Math.min(100, width), Math.min(100, height));
                const pixels = imageData.data;
                let totalBrightness = 0;
                let sampleCount = 0;
                
                // 采样检查亮度（每10个像素检查一次）
                for (let i = 0; i < pixels.length; i += 40) {
                  const r = pixels[i];
                  const g = pixels[i + 1];
                  const b = pixels[i + 2];
                  const brightness = (r + g + b) / 3;
                  totalBrightness += brightness;
                  sampleCount++;
                }
                
                const avgBrightness = totalBrightness / sampleCount;
                
                // 如果平均亮度太低（可能是黑屏），尝试seek到更后面的位置
                if (avgBrightness < 10 && video.duration > 1) {
                  console.log("检测到可能是黑屏，尝试seek到1秒位置");
                  video.currentTime = 1;
                  return; // 等待下一次seeked事件
                }
                
                console.log('[主线程] 开始异步编码图片...');
                
                // 使用异步的 toBlob 代替同步的 toDataURL
                canvas.toBlob(async (blob) => {
                  if (!blob) {
                    cleanup();
                    reject(new Error('无法生成图片数据'));
                    return;
                  }
                  
                  // 让出控制权
                  await new Promise(r => setTimeout(r, 0));
                  
                  // 异步读取为 Base64
                  const reader = new FileReader();
                  reader.onload = () => {
                    const dataUrl = reader.result;
                    if (dataUrl && dataUrl !== 'data:,') {
                      console.log('[主线程] ✅ 异步编码完成');
                      cleanup();
                      resolve(dataUrl);
                    } else {
                      cleanup();
                      reject(new Error('无法生成图片数据'));
                    }
                  };
                  reader.onerror = () => {
                    cleanup();
                    reject(new Error('读取图片失败'));
                  };
                  reader.readAsDataURL(blob);
                }, 'image/jpeg', 0.7);
              } catch (drawError) {
                cleanup();
                reject(new Error('无法从视频提取画面'));
              }
            } catch (err) {
              cleanup();
              reject(new Error('视频帧提取失败: ' + (err.message || '未知错误')));
            }
          }, 100); // 等待100ms确保帧已渲染
        } catch (err) {
          cleanup();
          reject(new Error('视频帧提取失败: ' + (err.message || '未知错误')));
        }
      };

      video.onerror = (e) => {
        cleanup();
        const errorMsg = video.error 
          ? `视频加载失败: ${video.error.code === 4 ? '格式不支持' : '文件损坏或无法读取'}`
          : '视频加载失败: 未知错误';
        reject(new Error(errorMsg));
      };
      
      // 开始加载视频
      video.load();
    });
  };

  // --- 备用提取方法（用于超时后的重试）---
  const extractFrameFromVideoFallback = (videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'none'; // 备用方法：不预加载
      
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.error("⏱️ 备用方法超时（5秒）");
          video.remove();
          reject(new Error('备用方法也超时'));
        }
      }, 5000); // 备用方法5秒超时

      const cleanup = () => {
        clearTimeout(timeout);
        if (!resolved) {
          resolved = true;
          setTimeout(() => video.remove(), 100);
        }
      };

      video.oncanplay = () => {
        if (resolved) return;
        try {
          const width = video.videoWidth || 640;
          const height = video.videoHeight || 480;
          
          const canvas = document.createElement('canvas');
          canvas.width = Math.min(width, 640); // 限制最大尺寸，加快处理
          canvas.height = Math.min(height, 480);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          const dataUrl = canvas.toDataURL('image/jpeg', 0.6); // 降低质量，加快处理
          cleanup();
          resolve(dataUrl);
        } catch (err) {
          cleanup();
          reject(err);
        }
      };

      video.onerror = () => {
        cleanup();
        reject(new Error('备用方法失败'));
      };
      
      video.load();
    });
  };

  // --- 核心逻辑 2: 播放列表轮播 ---
  const handleVideoEnded = () => {
    if (playlist.length > 1) {
      // 随机播放下一个，但尽量不重复当前
      let nextIndex;
      do {
        nextIndex = Math.floor(Math.random() * playlist.length);
      } while (nextIndex === currentIndex && playlist.length > 1);
      
      // 先淡出当前视频
      const currentVideo = document.getElementById('user-bg-video');
      if (currentVideo) {
        currentVideo.classList.remove('video-fade-in');
        currentVideo.style.opacity = '0';
        // 等待淡出动画完成后再切换
        setTimeout(() => {
          setCurrentIndex(nextIndex);
        }, 800);
      } else {
        setCurrentIndex(nextIndex);
      }
    } else {
      // 只有一个视频，循环播放
      const videoEl = document.getElementById('user-bg-video');
      if(videoEl) {
        videoEl.currentTime = 0;
        videoEl.play().catch(err => console.error("视频循环播放失败:", err));
      }
    }
  };
  
  // 当视频切换时，触发淡入淡出动画
  useEffect(() => {
    if (currentItem) {
      const video = document.getElementById('user-bg-video');
      if (video) {
        // 移除 visible 类，淡出
        video.classList.remove('visible');
        
        // 短暂延迟后添加 visible 类，触发淡入
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            video.classList.add('visible');
            console.log("✅ 背景视频淡入");
          });
        });
        
        // 确保视频播放
        video.play().catch(err => console.log("播放:", err));
      }
    }
  }, [currentIndex, currentItem]);
  

  // 确保鱼的视频和 WebGL 渲染始终运行
  useEffect(() => {
    console.log("🐠 启动鱼视频 + WebGL 监控机制...");
    
    let lastVideoTime = 0;
    let sameTimeCount = 0;
    
    const checkAndPlayFish = () => {
      // 检查 video 元素
      const fishVideo = document.querySelector('video[src*="luma_matte"]');
      // 检查 canvas 元素
      const fishCanvas = document.querySelector('.fish-layer canvas');
      
      if (fishVideo) {
        const isPaused = fishVideo.paused;
        const currentTime = fishVideo.currentTime;
        const readyState = fishVideo.readyState;
        
        // 检测视频是否真的在播放（时间是否在变化）
        if (currentTime === lastVideoTime && !isPaused) {
          sameTimeCount++;
          if (sameTimeCount > 5) {
            console.warn('🐠 视频时间没有变化，可能卡死了！尝试重启...');
            fishVideo.currentTime = 0;
            fishVideo.play()
              .then(() => {
                console.log('✅ 鱼视频重启成功');
                sameTimeCount = 0;
              })
              .catch(err => console.error('重启失败:', err));
          }
        } else {
          sameTimeCount = 0;
        }
        lastVideoTime = currentTime;
        
        // 如果暂停了，立即恢复
        if (isPaused && readyState >= 2) {
          console.log('🐠 检测到鱼视频暂停，尝试恢复播放...');
          fishVideo.play()
            .then(() => console.log('✅ 鱼视频恢复播放成功'))
            .catch(err => console.warn('鱼视频恢复失败:', err));
        }
        
        // 每10秒记录一次状态（用于调试）
        if (Math.floor(Date.now() / 10000) !== Math.floor((Date.now() - 500) / 10000)) {
          console.log('🐠 鱼视频状态:', {
            暂停: isPaused,
            当前时间: currentTime.toFixed(2),
            就绪状态: readyState,
            Canvas存在: !!fishCanvas,
            时间变化正常: sameTimeCount === 0
          });
        }
      }
    };
    
    // 每500ms检查一次
    const interval = setInterval(checkAndPlayFish, 500);
    
    return () => {
      console.log("🐠 停止监控");
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="app-container">
      
      {/* 任务清单 (最顶层) */}
      <div className="task-list-container">
        <div 
          className="task-list-header" 
          onClick={() => setTaskListExpanded(!taskListExpanded)}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <span>任务清单</span>
          <span className="task-list-toggle">{taskListExpanded ? '▼' : '▶'}</span>
        </div>
        {taskListExpanded && (
        <div className="task-list-content">
          {/* 已完成的任务 */}
          {completedTasks.length > 0 && (
            <div className="task-section">
              <div className="task-section-title">已完成 ({completedTasks.length})</div>
              <div className="task-items completed">
                {completedTasks.map((task, idx) => (
                  <div key={idx} className="task-item completed">
                    <span className="task-check">✓</span>
                    <span className="task-text">{task}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* 正在播放的视频 */}
          {currentPlayingTask && (
            <div className="task-section">
              <div className="task-section-title">正在播放</div>
              <div className="task-item playing">
                <span className="task-indicator">▶</span>
                <span className="task-text">{currentPlayingTask}</span>
              </div>
            </div>
          )}
          
          {/* 待完成的任务 */}
          {pendingTasks.length > 0 && (
            <div className="task-section">
              <div className="task-section-title">待完成 ({pendingTasks.length})</div>
              <div className="task-items pending">
                {pendingTasks.map((task, idx) => (
                  <div key={idx} className="task-item pending">
                    <span className="task-dot">•</span>
                    <span className="task-text">{task}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* 推荐的下一个任务 */}
          {nextRecommendedTask && (
            <div className="task-section">
              <div className="task-section-title">推荐下一个</div>
              <div className="task-item recommended">
                <span className="task-star">★</span>
                <span className="task-text">{nextRecommendedTask}</span>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      
      {/* 摄像头预览层（如果启用） */}
      {showCameraPreview && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 100,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {/* 录制状态和时间码显示 */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 101,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px'
          }}>
            {isRecording && (
              <>
                <div style={{
                  background: 'rgba(255, 0, 0, 0.8)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    width: '10px',
                    height: '10px',
                    background: '#fff',
                    borderRadius: '50%',
                    animation: 'pulse 1s infinite'
                  }}></span>
                  录制中
                </div>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: '#00ffff',
                  padding: '6px 12px',
                  borderRadius: '12px',
                  fontSize: '24px',
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  textShadow: '0 0 10px rgba(0, 255, 255, 0.8)'
                }}>
                  {formatTime(recordingTime)} / 00:05
                </div>
              </>
            )}
          </div>
          
          <video
            ref={videoPreviewRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '90%',
              maxWidth: '600px',
              height: 'auto',
              borderRadius: '12px'
              // 移除镜像，保持原始方向
            }}
          />
          <div style={{
            marginTop: '20px',
            display: 'flex',
            gap: '20px',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {!isRecording ? (
              <>
                <button
                  className="action-btn"
                  onClick={() => {
                    const isTaskMode = window.currentTaskMode || false;
                    stopCameraAndRecord(isTaskMode);
                  }}
                  style={{
                    background: 'rgba(0, 255, 0, 0.3)',
                    borderColor: '#00ff00',
                    fontSize: '18px',
                    padding: '12px 24px'
                  }}
                >
                  开始录制（5秒）
                </button>
                <button
                  className="action-btn"
                  onClick={switchCamera}
                  style={{
                    background: 'rgba(0, 150, 255, 0.3)',
                    borderColor: '#0096ff',
                    fontSize: '18px',
                    padding: '12px 24px'
                  }}
                >
                  {facingMode === 'environment' ? '📷 前置' : '📷 后置'}
                </button>
                <button
                  className="action-btn"
                  onClick={cancelCameraPreview}
                  style={{
                    background: 'rgba(255, 0, 0, 0.3)',
                    borderColor: '#ff0000',
                    fontSize: '18px',
                    padding: '12px 24px'
                  }}
                >
                  取消
                </button>
              </>
            ) : (
              <div style={{
                color: '#fff',
                fontSize: '16px',
                padding: '12px 24px'
              }}>
                正在录制...
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* 1. 用户背景视频层 (底层) */}
      {currentItem ? (
        <video
          id="user-bg-video"
          key={currentItem.id}
          className="user-video-bg"
          src={currentItem.url}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={handleVideoEnded} // 播放完自动切下一个
          onLoadedMetadata={(e) => {
            // 元数据加载完成，确保视频以原始分辨率渲染
            const video = e.target;
            if (video && video.videoWidth > 0 && video.videoHeight > 0) {
              console.log(`视频元数据分辨率: ${video.videoWidth}x${video.videoHeight}`);
              // 设置视频的实际宽高，确保以原始分辨率渲染
              video.width = video.videoWidth;
              video.height = video.videoHeight;
              // 强制重新渲染
              video.style.width = '100%';
              video.style.height = '100%';
            }
          }}
          onLoadedData={(e) => {
            // 视频数据加载完成，确保能播放并保持高分辨率
            const video = e.target;
            if (video) {
              // 确保视频以原始分辨率渲染
              if (video.videoWidth > 0 && video.videoHeight > 0) {
                console.log(`视频实际分辨率: ${video.videoWidth}x${video.videoHeight}`);
                // 再次确认尺寸设置
                video.width = video.videoWidth;
                video.height = video.videoHeight;
              }
              video.play().catch(err => console.error("视频播放失败:", err));
            }
          }}
          onCanPlay={(e) => {
            // 视频可以播放时，确保高质量渲染
            const video = e.target;
            if (video && video.videoWidth > 0 && video.videoHeight > 0) {
              // 确保使用原始分辨率
              video.width = video.videoWidth;
              video.height = video.videoHeight;
            }
          }}
        />
      ) : (
        // 空状态：纯黑背景
        <div className="user-video-bg" style={{background: '#000'}}></div>
      )}

      {/* 2. 鱼的视频层 (中间层) - Three.js Shader 专家方案 */}
      {/* Side-by-Side 渲染，Three.js 管理渲染循环 */}
      <ThreeFishVideo
        src="/fish_luma_matte.mp4"
        className="fish-layer"
      />

      {/* 3. UI 交互层 (顶层) */}
      <div className="ui-layer">
        {loading && (
          <div className="loading-indicator">
            Floater 正在好奇地观察...
          </div>
        )}

        {/* 显示当前的观察 */}
        {!loading && currentItem && currentItem.roast && (
          <div className="roast-container" style={{
            display: 'flex',
            zIndex: 100,
            opacity: 1,
            visibility: 'visible',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '20px',
            marginBottom: '40px'
          }}>
            <div className="roast-text">{currentItem.roast}</div>
          </div>
        )}
        
        {/* 如果观察为空，显示提示 */}
        {!loading && currentItem && !currentItem.roast && currentItem.url && (
          <div className="roast-container">
            <div className="roast-text" style={{color: '#ff6b6b'}}>
              （观察加载失败，请检查控制台）
            </div>
          </div>
        )}
        
        {/* 如果没有视频，显示提示 */}
        {!loading && !currentItem && playlist.length === 0 && (
          <div className="roast-container">
            <div className="roast-text" style={{color: '#888'}}>
              上传视频，让 Floater 看看你的世界
            </div>
          </div>
        )}
      </div>
      
      {/* 当前任务显示框（主UI上，一直显示） */}
      <div className="current-task-box-main">
        <div className="current-task-label">当前任务</div>
        <div className="current-task-text">{currentTask}</div>
      </div>

      {/* 独立的按钮区域 (右上角) */}
      <div className="upload-btn-wrapper">
        <button 
          className="action-btn" 
          onClick={() => {
            // 直接打开文件选择器（从相册选择）
            fileInputRef.current.click();
          }}
        >
          📁 从相册选择
        </button>
        <button 
          className="action-btn" 
          onClick={() => {
            // 尝试使用摄像头 API 获取高分辨率视频
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
              handleCameraCapture();
            } else {
              // 如果不支持，提示用户
              alert('您的浏览器不支持摄像头录制，请使用"从相册选择"功能');
            }
          }}
          style={{marginTop: '10px'}}
        >
          📷 摄像头录制
        </button>
        <button 
          className="action-btn" 
          onClick={() => {
            setIsTaskMode(true);
            window.currentTaskMode = true; // 临时标记
            // 完成任务：优先使用摄像头，否则从相册选择
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
              handleCameraCapture(true);
            } else {
              fileInputRef.current.click();
            }
          }}
          style={{marginTop: '10px'}}
        >
          ✓ 完成当前任务
        </button>
        <input 
          type="file" 
          accept="video/*" 
          ref={fileInputRef} 
          style={{display: 'none'}}
          onChange={handleFileUpload}
          onClick={(e) => {
            // 重置 input，允许重复选择同一文件
            e.target.value = '';
          }}
        />
      </div>
      
    </div>
  );
}

export default App;
// Cloudflare Pages 部署配置已更新
// 最终部署测试 - Mon Dec  8 20:19:10 CST 2025